import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Users, Target, LineChart, FileText,
  Crown, Network, FileBarChart, ClipboardCheck,
  TrendingUp, Search, Euro, Presentation,
} from "lucide-react";
import useReveal from "@/hooks/useReveal";

const SERVICES = [
  {
    id: "consulenza-aziendale",
    n: "01",
    title: "Consulenza Aziendale",
    intro:
      "Affianchiamo la tua azienda con soluzioni su misura per migliorare le performance, ottimizzare i processi e affrontare le sfide del mercato.",
    items: [
      {
        icon: Users,
        k: "Analisi e diagnosi",
        v: "Valutiamo la situazione attuale e individuiamo le aree di miglioramento.",
      },
      {
        icon: Target,
        k: "Soluzioni personalizzate",
        v: "Progettiamo strategie su misura per raggiungere i tuoi obiettivi.",
      },
      {
        icon: TrendingUp,
        k: "Crescita e competitività",
        v: "Ti supportiamo per ottenere risultati concreti e duraturi.",
      },
    ],
    payoff: ["La tua crescita,", "il nostro obiettivo."],
    image: "/servizio-consulenza-aziendale.jpg",
  },
  {
    id: "pianificazione-strategica",
    n: "02",
    title: "Pianificazione Strategica",
    intro:
      "Definiamo insieme obiettivi chiari e strategie vincenti per guidare la tua azienda verso una crescita sostenibile e duratura.",
    items: [
      {
        icon: Target,
        k: "Analisi del contesto e del mercato",
        v: "Studiamo il tuo settore, i trend e i competitor per individuare opportunità concrete.",
      },
      {
        icon: Crown,
        k: "Definizione di obiettivi e azioni strategiche",
        v: "Trasformiamo la visione in obiettivi misurabili e piani d'azione efficaci.",
      },
      {
        icon: Network,
        k: "Piani operativi su misura",
        v: "Sviluppiamo piani dettagliati e personalizzati per massimizzare risorse e risultati.",
      },
      {
        icon: LineChart,
        k: "Monitoraggio e miglioramento continuo",
        v: "Monitoriamo i progressi e adattiamo le strategie per garantire risultati costanti nel tempo.",
      },
    ],
    payoff: ["Strategie chiare.", "Risultati concreti."],
    image: "/servizio-pianificazione-strategica.jpg",
  },
  {
    id: "business-plan",
    n: "03",
    title: "Business Plan su Misura",
    intro:
      "Ti aiutiamo a trasformare la tua idea in un progetto concreto con un business plan professionale, chiaro e convincente.",
    items: [
      {
        icon: Target,
        k: "Analisi di mercato e competitor",
        v: "Studiamo il mercato, i trend e i competitor per definire opportunità e vantaggi competitivi.",
      },
      {
        icon: FileText,
        k: "Strategia e piano operativo",
        v: "Definiamo obiettivi, strategie e azioni operative per garantire una crescita sostenibile.",
      },
      {
        icon: FileBarChart,
        k: "Piano economico e finanziario",
        v: "Prevediamo costi, ricavi e investimenti per valutare la sostenibilità e la redditività del progetto.",
      },
      {
        icon: Presentation,
        k: "Documento professionale e convincente",
        v: "Realizziamo un business plan chiaro, completo e pronto per essere presentato a banche, investitori e partner.",
      },
    ],
    payoff: ["La tua idea.", "Il nostro piano.", "Il tuo successo."],
    image: "/servizio-business-plan.jpg",
  },
  {
    id: "finanza-agevolata",
    n: "04",
    title: "Finanza Agevolata",
    intro:
      "Ti aiutiamo ad accedere a bandi e finanziamenti per sostenere i tuoi progetti e far crescere la tua azienda.",
    items: [
      {
        icon: Search,
        k: "Ricerca bandi e opportunità",
        v: "Screening continuo di bandi regionali, nazionali ed europei compatibili con il tuo progetto.",
      },
      {
        icon: ClipboardCheck,
        k: "Gestione pratiche e documentazione",
        v: "Predisponiamo, presentiamo e seguiamo l'iter burocratico end-to-end.",
      },
      {
        icon: Euro,
        k: "Accesso a contributi e incentivi",
        v: "Massimizziamo l'accesso a contributi a fondo perduto, finanziamenti agevolati e crediti d'imposta.",
      },
    ],
    payoff: ["Capitale per crescere.", "Senza diluire l'equity."],
    image: "/servizio-finanza-agevolata.jpg",
  },
];

export default function Services() {
  useReveal();
  return (
    <div data-testid="page-services">
      {/* Banda navy decorativa — separa header dalla sezione /servizi */}
      <section
        data-testid="services-top-band"
        className="bv-network text-white"
        aria-hidden="true"
      >
        <div className="bv-container py-10 md:py-12" />
      </section>

      {/* SEZIONE 1 — HERO SERVIZI */}
      <section className="bv-container pt-20 md:pt-28 pb-16" data-testid="services-hero">
        <p className="overline reveal" data-testid="services-hero-overline">/ Servizi</p>
        <h1
          data-testid="services-hero-title"
          className="h-display text-4xl md:text-7xl mt-6 max-w-[20ch] leading-[0.98] reveal"
        >
          <span style={{ color: "#2BA8AD" }}>Risultati misurabili.</span><br />
          Niente di più,<br />
          niente di meno.
        </h1>
        <p
          data-testid="services-hero-sub"
          className="mt-8 max-w-2xl text-[17px] text-[#0E2E4D]/75 reveal"
        >
          Ogni servizio è un percorso strutturato, con deliverable chiari e un risultato
          definito a inizio mandato.
        </p>
      </section>

      {SERVICES.map((s, i) => (
        <ServiceBlock key={s.id} s={s} index={i} />
      ))}

      {/* SEZIONE 3 — CTA FINALE SERVIZI */}
      <section className="bv-network text-white" data-testid="services-final-cta">
        <div className="bv-container py-24 md:py-32 grid grid-cols-12 gap-8 items-end">
          <div className="col-span-12 md:col-span-8 reveal">
            <h2
              data-testid="services-final-cta-title"
              className="h-display text-4xl md:text-6xl leading-[1.02] max-w-[22ch]"
            >
              Scopri quale soluzione si adatta meglio alla tua azienda
            </h2>
          </div>
          <div className="col-span-12 md:col-span-4 flex md:justify-end reveal">
            <Link
              to="/contatti"
              data-testid="services-cta-final"
              className="inline-flex items-center gap-3 border border-white text-white px-8 py-5 text-[12px] tracking-[0.20em] uppercase font-display font-bold hover:bg-white hover:text-[#0E2E4D] transition-colors"
            >
              Richiedi una consulenza <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function ServiceBlock({ s, index }) {
  const reverse = index % 2 === 1;
  return (
    <section
      id={s.id}
      data-testid={`service-section-${s.n}`}
      className={`scroll-mt-24 ${reverse ? "bg-[#ECE7DC]" : "bg-[#F5F2EB]"}`}
    >
      <div className="bv-container py-24 md:py-32">
        <div className={`grid grid-cols-12 gap-10 ${reverse ? "lg:[direction:rtl]" : ""}`}>
          {/* Text side */}
          <div className={`col-span-12 lg:col-span-7 reveal ${reverse ? "lg:[direction:ltr]" : ""}`}>
            <p className="font-mono text-[12px] tracking-[0.24em] text-[#2BA8AD]">{`/ ${s.n}`}</p>
            <h2 className="h-display text-4xl md:text-6xl mt-5 leading-[1] max-w-[16ch]">{s.title}</h2>
            <div className="mt-4 h-[3px] w-16 bg-[#2BA8AD]" />
            <p className="mt-8 text-[17px] text-[#0E2E4D]/85 leading-relaxed max-w-prose">{s.intro}</p>

            <ul className="mt-12 space-y-8">
              {s.items.map((it, idx) => {
                const Icon = it.icon;
                return (
                  <li key={idx} className="flex items-start gap-5" data-testid={`service-${s.n}-item-${idx}`}>
                    <span className="bv-icon-ring">
                      <Icon size={20} strokeWidth={1.6} />
                    </span>
                    <div>
                      <p className="font-display font-extrabold text-[15px] tracking-[0.04em] uppercase text-[#0E2E4D]">{it.k}</p>
                      <p className="mt-2 text-[15px] text-[#0E2E4D]/75 leading-relaxed max-w-xl">{it.v}</p>
                    </div>
                  </li>
                );
              })}
            </ul>

            {/* Payoff */}
            <div className="mt-14 pt-10 border-t border-[#0E2E4D]/15">
              <p className="bv-tagline text-2xl md:text-3xl text-[#0E2E4D] leading-tight">
                {s.payoff.map((line, idx) => (
                  <span key={idx} className="block">
                    {idx === s.payoff.length - 1 ? <em>{line}</em> : line}
                  </span>
                ))}
              </p>
            </div>

            <Link
              to="/contatti"
              data-testid={`service-cta-${s.n}`}
              className="mt-12 inline-flex items-center gap-2 bg-[#0E2E4D] text-white px-7 py-4 text-[12px] tracking-[0.18em] uppercase font-display font-bold hover:bg-[#2BA8AD] transition-colors"
            >
              Parla con un partner <ArrowUpRight size={14} />
            </Link>
          </div>

          {/* Image / network side */}
          <div className={`col-span-12 lg:col-span-5 reveal ${reverse ? "lg:[direction:ltr]" : ""}`} style={{ "--reveal-delay": "120ms" }}>
            <div className="relative aspect-[4/5] bv-network overflow-hidden border border-[#0E2E4D]/15">
              <img src={s.image} alt={s.title} className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
